import { List } from '@refinedev/antd';
import {
  Table,
  Input,
  Space,
  Tag,
  Avatar,
  Button,
  Card,
  Drawer,
  Descriptions,
  Select,
  Tooltip,
  App as AntApp,
} from 'antd';
import {
  SearchOutlined,
  UserOutlined,
  ReloadOutlined,
  FilePdfOutlined,
  CreditCardOutlined,
} from '@ant-design/icons';
import { useEffect, useState, useCallback } from 'react';
import dayjs from 'dayjs';
import { apiClient } from '../../lib/api-client';

// Mirror of the backend GET /admin/osago-policies row shape. See
// AdminService.listOsagoPolicies for the source of truth.
interface OsagoPolicyRow {
  id: number;
  driverId: number;
  vehicleId: number | null;
  orderId: string;
  status: 'PENDING' | 'PAID' | 'FAILED' | 'CANCELED' | 'NOT_FOUND';
  amountUzs: number;
  gosNumber: string;
  policyNumber: string | null;
  periodId: number;
  numberDriversId: number;
  beginDate: string | null;
  endDate: string | null;
  pdfUrl: string | null;
  clickUrl: string | null;
  paymeUrl: string | null;
  createdAt: string;
  updatedAt: string;
  driver: {
    id: number;
    fullName: string | null;
    phone: string;
    photoUrl: string | null;
  };
  vehicle: {
    id: number;
    make: string;
    model: string;
    plate: string;
  } | null;
}

interface OsagoPolicyDetail extends OsagoPolicyRow {
  anketaId: string | null;
  techSery: string;
  techNumber: string;
  vehicleTypeId: number;
  rawSaveResponse: unknown;
  rawStatusResponse: unknown;
}

// Visual hierarchy at a glance: PAID green, PENDING orange (action
// needed), FAILED / CANCELED red, NOT_FOUND gray.
const statusColors: Record<OsagoPolicyRow['status'], string> = {
  PAID: 'green',
  PENDING: 'orange',
  FAILED: 'red',
  CANCELED: 'red',
  NOT_FOUND: 'default',
};

const fmtUzs = (n: number) =>
  `${n.toLocaleString('ru-RU').replace(/,/g, ' ')} so'm`;

export const OsagoPolicyList = () => {
  const { message } = AntApp.useApp();

  const [rows, setRows] = useState<OsagoPolicyRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] =
    useState<OsagoPolicyRow['status'] | undefined>(undefined);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState<OsagoPolicyDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [repolling, setRepolling] = useState(false);
  const [checkingId, setCheckingId] = useState<number | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get<{
        data: OsagoPolicyRow[];
        meta: { total: number };
      }>('/admin/osago-policies', {
        params: {
          page,
          limit: pageSize,
          status: statusFilter,
          search: search.trim() || undefined,
        },
      });
      setRows(res.data?.data ?? []);
      setTotal(res.data?.meta?.total ?? 0);
    } catch (err: any) {
      message.error(
        err?.response?.data?.message || "Ro'yxatni yuklashda xatolik",
      );
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, statusFilter, search, message]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const openDetail = async (row: OsagoPolicyRow) => {
    setDetailOpen(true);
    setDetail(null);
    setDetailLoading(true);
    try {
      const res = await apiClient.get<OsagoPolicyDetail>(
        `/admin/osago-policies/${row.id}`,
      );
      setDetail(res.data);
    } catch (err: any) {
      message.error(
        err?.response?.data?.message || "Polis ma'lumotini olishda xatolik",
      );
    } finally {
      setDetailLoading(false);
    }
  };

  const handleRepoll = async () => {
    if (!detail) return;
    setRepolling(true);
    try {
      const res = await apiClient.post<OsagoPolicyRow>(
        `/admin/osago-policies/${detail.id}/repoll`,
      );
      if (res.data?.status === 'PAID') {
        message.success('Polis to\'langan deb belgilandi');
      } else {
        message.info(`Holat: ${res.data?.status}`);
      }
      // Refresh both the detail drawer and the underlying list row.
      await openDetail(detail);
      fetchList();
    } catch (err: any) {
      message.error(
        err?.response?.data?.message || 'Qaytadan tekshirishda xatolik',
      );
    } finally {
      setRepolling(false);
    }
  };

  // Per-row "Tekshirish" — the same status re-poll as the drawer's
  // "Qaytadan tekshirish", but inline in the list so an admin can
  // reconcile a long-tail payment (driver paid the Click/Payme link
  // after the 30-min background-poller window lapsed) without having to
  // open each policy. Updates just the clicked row in place.
  const handleCheckRow = async (row: OsagoPolicyRow) => {
    setCheckingId(row.id);
    try {
      const res = await apiClient.post<{
        status: OsagoPolicyRow['status'];
        policyNumber: string | null;
        beginDate: string | null;
        endDate: string | null;
        pdfUrl: string | null;
      }>(`/admin/osago-policies/${row.id}/repoll`);
      const u = res.data;
      setRows((prev) =>
        prev.map((r) =>
          r.id === row.id
            ? {
                ...r,
                status: u.status,
                policyNumber: u.policyNumber ?? r.policyNumber,
                beginDate: u.beginDate ?? r.beginDate,
                endDate: u.endDate ?? r.endDate,
                pdfUrl: u.pdfUrl ?? r.pdfUrl,
              }
            : r,
        ),
      );
      if (u.status === 'PAID') {
        message.success("Polis to'langan ✓");
      } else {
        message.info(`Hali to'lanmagan — holat: ${u.status}`);
      }
    } catch (err: any) {
      message.error(
        err?.response?.data?.message || 'Tekshirishda xatolik',
      );
    } finally {
      setCheckingId(null);
    }
  };

  return (
    <List title="OSAGO sotib olishlar">
      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Telefon, plita, order ID yoki polis raqami"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onPressEnter={() => {
              setPage(1);
              fetchList();
            }}
            style={{ width: 320 }}
          />
          <Select<OsagoPolicyRow['status'] | undefined>
            placeholder="Holat"
            allowClear
            value={statusFilter}
            onChange={(v) => {
              setStatusFilter(v);
              setPage(1);
            }}
            style={{ width: 160 }}
            options={[
              { value: 'PENDING', label: 'PENDING' },
              { value: 'PAID', label: 'PAID' },
              { value: 'FAILED', label: 'FAILED' },
              { value: 'CANCELED', label: 'CANCELED' },
            ]}
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              setPage(1);
              fetchList();
            }}
          >
            Yangilash
          </Button>
        </Space>
      </Card>

      <Table<OsagoPolicyRow>
        rowKey="id"
        loading={loading}
        dataSource={rows}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          onChange: (p, s) => {
            setPage(p);
            setPageSize(s);
          },
        }}
        onRow={(row) => ({
          onClick: () => openDetail(row),
          style: { cursor: 'pointer' },
        })}
        columns={[
          {
            title: 'Holat',
            dataIndex: 'status',
            width: 110,
            render: (status: OsagoPolicyRow['status']) => (
              <Tag color={statusColors[status]}>{status}</Tag>
            ),
          },
          {
            title: 'Haydovchi',
            render: (_: unknown, row) => (
              <Space>
                <Avatar
                  size={28}
                  src={row.driver.photoUrl || undefined}
                  icon={<UserOutlined />}
                />
                <div>
                  <div style={{ fontWeight: 500 }}>
                    {row.driver.fullName || '—'}
                  </div>
                  <div style={{ color: '#8C8C8C', fontSize: 12 }}>
                    {row.driver.phone}
                  </div>
                </div>
              </Space>
            ),
          },
          {
            title: 'Mashina',
            render: (_: unknown, row) =>
              row.vehicle ? (
                <Tooltip title={`${row.vehicle.make} ${row.vehicle.model}`}>
                  <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {row.vehicle.plate}
                  </span>
                </Tooltip>
              ) : (
                <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                  {row.gosNumber}
                </span>
              ),
          },
          {
            title: 'Muddati',
            dataIndex: 'periodId',
            width: 90,
            render: (periodId: number) =>
              periodId === 1 ? '12 oy' : periodId === 2 ? '6 oy' : '—',
          },
          {
            title: 'Polis #',
            dataIndex: 'policyNumber',
            render: (n: string | null) =>
              n ? (
                <span style={{ fontFamily: 'monospace' }}>{n}</span>
              ) : (
                <span style={{ color: '#BFBFBF' }}>—</span>
              ),
          },
          {
            title: 'Summa',
            dataIndex: 'amountUzs',
            width: 140,
            align: 'right',
            render: (n: number) => fmtUzs(n),
          },
          {
            title: 'Sotib olingan',
            dataIndex: 'createdAt',
            width: 150,
            render: (d: string) => dayjs(d).format('DD.MM.YYYY HH:mm'),
          },
          {
            title: 'Amallar',
            width: 130,
            // Only the not-yet-paid states are worth re-checking. PAID is
            // terminal; FAILED / CANCELED won't become paid by polling.
            render: (_: unknown, row) =>
              row.status === 'PENDING' || row.status === 'NOT_FOUND' ? (
                <Tooltip title="To'lov holatini tekshirish">
                  <Button
                    size="small"
                    icon={<ReloadOutlined />}
                    loading={checkingId === row.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleCheckRow(row);
                    }}
                  >
                    Tekshirish
                  </Button>
                </Tooltip>
              ) : null,
          },
        ]}
      />

      <Drawer
        title={
          detail
            ? `OSAGO polisi #${detail.id} — ${detail.status}`
            : 'Polis batafsil'
        }
        open={detailOpen}
        onClose={() => {
          setDetailOpen(false);
          setDetail(null);
        }}
        width={620}
        extra={
          detail ? (
            <Space>
              {detail.pdfUrl && (
                <Button
                  icon={<FilePdfOutlined />}
                  href={detail.pdfUrl}
                  target="_blank"
                >
                  PDF
                </Button>
              )}
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                loading={repolling}
                onClick={handleRepoll}
                disabled={detail.status === 'PAID'}
              >
                Qaytadan tekshirish
              </Button>
            </Space>
          ) : null
        }
      >
        {detailLoading || !detail ? (
          <div style={{ padding: 24 }}>Yuklanmoqda…</div>
        ) : (
          <>
            <Descriptions
              title="Haydovchi"
              column={1}
              size="small"
              bordered
              style={{ marginBottom: 16 }}
            >
              <Descriptions.Item label="Ism">
                {detail.driver.fullName || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Telefon">
                {detail.driver.phone}
              </Descriptions.Item>
            </Descriptions>

            <Descriptions
              title="Polis"
              column={1}
              size="small"
              bordered
              style={{ marginBottom: 16 }}
            >
              <Descriptions.Item label="Holat">
                <Tag color={statusColors[detail.status]}>{detail.status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Order ID">
                <span style={{ fontFamily: 'monospace' }}>
                  {detail.orderId}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Anketa ID">
                {detail.anketaId || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Polis raqami">
                {detail.policyNumber || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Davlat raqami">
                <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                  {detail.gosNumber}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Texpassport">
                {detail.techSery} {detail.techNumber}
              </Descriptions.Item>
              <Descriptions.Item label="Muddati">
                {detail.periodId === 1 ? '12 oy' : '6 oy'} (
                {detail.numberDriversId === 1
                  ? 'cheklanmagan'
                  : 'cheklangan'}
                )
              </Descriptions.Item>
              <Descriptions.Item label="Davri">
                {detail.beginDate
                  ? dayjs(detail.beginDate).format('DD.MM.YYYY')
                  : '—'}{' '}
                —{' '}
                {detail.endDate
                  ? dayjs(detail.endDate).format('DD.MM.YYYY')
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Summa">
                {fmtUzs(detail.amountUzs)}
              </Descriptions.Item>
              <Descriptions.Item label="Sotib olingan">
                {dayjs(detail.createdAt).format('DD.MM.YYYY HH:mm')}
              </Descriptions.Item>
            </Descriptions>

            {(detail.clickUrl || detail.paymeUrl) && (
              <Card
                size="small"
                title={
                  <Space>
                    <CreditCardOutlined />
                    <span>To'lov havolalari</span>
                  </Space>
                }
                style={{ marginBottom: 16 }}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  {detail.clickUrl && (
                    <a
                      href={detail.clickUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Click: {detail.clickUrl}
                    </a>
                  )}
                  {detail.paymeUrl && (
                    <a
                      href={detail.paymeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Payme: {detail.paymeUrl}
                    </a>
                  )}
                </Space>
              </Card>
            )}

            <Card
              size="small"
              title="Gross.uz so'rovi (raw)"
              style={{ marginBottom: 16 }}
            >
              <pre
                style={{
                  background: '#f5f5f5',
                  padding: 8,
                  fontSize: 11,
                  maxHeight: 240,
                  overflow: 'auto',
                  margin: 0,
                }}
              >
                {JSON.stringify(detail.rawSaveResponse, null, 2)}
              </pre>
            </Card>
            {detail.rawStatusResponse && (
              <Card size="small" title="Gross.uz oxirgi holat (raw)">
                <pre
                  style={{
                    background: '#f5f5f5',
                    padding: 8,
                    fontSize: 11,
                    maxHeight: 240,
                    overflow: 'auto',
                    margin: 0,
                  }}
                >
                  {JSON.stringify(detail.rawStatusResponse, null, 2)}
                </pre>
              </Card>
            )}
          </>
        )}
      </Drawer>
    </List>
  );
};
